import { useEffect, useRef, useState } from 'react'
import { BookOpen, FileText, Pause, Play, RotateCcw, Settings, Volume2, VolumeX } from 'lucide-react'
import ReactPlayer from 'react-player'
import type { Technique } from '../types/Technique'
import Modal from './Modal'
import PDFViewer from './PDFViewer'

interface TechniqueModalProps {
  technique: Technique | null
  isOpen: boolean
  onClose: () => void
}

const beltLabels: Record<string, string> = {
  BLANCHE: 'Blanche',
  JAUNE: 'Jaune',
  ORANGE: 'Orange',
  VERTE: 'Verte',
  BLEUE: 'Bleue',
  MARRON: 'Marron',
  NOIRE: 'Noire',
}

const beltClasses: Record<string, string> = {
  BLANCHE: 'belt-white',
  JAUNE: 'belt-yellow',
  ORANGE: 'belt-orange',
  VERTE: 'belt-green',
  BLEUE: 'belt-blue',
  MARRON: 'belt-brown',
  NOIRE: 'belt-black',
}

const techniqueLabels: Record<string, string> = {
  KIHON: 'Kihon',
  KATA: 'Kata',
  SELF_DEFENSE: 'Self-défense',
  BASES: 'Bases',
  CHUTES: 'Chutes',
  AUTRE: 'Autre',
}

const TechniqueModal = ({ technique, isOpen, onClose }: TechniqueModalProps) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(0.8)
  const [isMuted, setIsMuted] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [showSettings, setShowSettings] = useState(false)
  const playerRef = useRef<HTMLVideoElement>(null)

  const playbackRates = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]

  useEffect(() => {
    if (!isOpen) {
      setIsPlaying(false)
      setShowSettings(false)
    }
  }, [isOpen])

  if (!technique) return null

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number(event.target.value)
    setVolume(newVolume)
    setIsMuted(newVolume === 0)
  }

  const resetPlayer = () => {
    setIsPlaying(false)
    setPlaybackRate(1)
    setVolume(0.8)
    setIsMuted(false)
    setShowSettings(false)
    if (playerRef.current) playerRef.current.currentTime = 0
  }

  const getBeltLabel = (beltLevel: string) => beltLabels[beltLevel] || beltLevel
  const getBeltClass = (beltLevel: string) => beltClasses[beltLevel] || 'belt-white'
  const getTypeLabel = (type: string) => techniqueLabels[type] || type

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={technique.name}
      eyebrow="Fiche technique"
      size="lg"
      footer={<button type="button" className="btn-secondary" onClick={onClose}>Fermer</button>}
    >
      <div className="space-y-6">
        <section className="modal-section p-4 sm:p-5">
          <div className="mb-3 flex flex-wrap gap-1.5">
            <span className={`belt-chip ${getBeltClass(technique.beltLevel)}`}>
              Ceinture {getBeltLabel(technique.beltLevel).toLowerCase()}
            </span>
            <span className="support-chip"><BookOpen className="h-3.5 w-3.5" aria-hidden="true" /> {getTypeLabel(technique.type)}</span>
          </div>
          <h3 className="text-lg font-extrabold text-gray-900">Description</h3>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-7 text-gray-700">{technique.description}</p>
        </section>

        {technique.media.imageUrl && (
          <figure className="overflow-hidden rounded-[1.35rem] border border-emerald-950/10 bg-emerald-950">
            <img
              src={technique.media.imageUrl}
              alt={`Illustration de la technique ${technique.name}`}
              className="max-h-[28rem] w-full object-contain"
            />
          </figure>
        )}

        {technique.media.videoUrl && (
          <section aria-labelledby="technique-video-title">
            <div className="mb-3 flex items-center gap-3">
              <span className="card-icon !h-10 !w-10 !rounded-[13px]" aria-hidden="true"><Play /></span>
              <div>
                <p className="eyebrow !mb-0.5">Démonstration</p>
                <h3 id="technique-video-title" className="text-lg font-extrabold text-gray-900">Vidéo de la technique</h3>
              </div>
            </div>

            <div className="video-player-container">
              <ReactPlayer
                ref={playerRef}
                src={technique.media.videoUrl}
                width="100%"
                height="100%"
                playing={isPlaying}
                volume={isMuted ? 0 : volume}
                playbackRate={playbackRate}
                controls={false}
              />

              <div className="video-controls" aria-label="Contrôles de la vidéo">
                <button
                  type="button"
                  className="media-control"
                  onClick={() => setIsPlaying((current) => !current)}
                  aria-label={isPlaying ? 'Mettre la vidéo en pause' : 'Lire la vidéo'}
                  title={isPlaying ? 'Pause' : 'Lecture'}
                >
                  {isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="media-control"
                    onClick={() => setIsMuted((current) => !current)}
                    aria-label={isMuted ? 'Activer le son' : 'Couper le son'}
                    title={isMuted ? 'Activer le son' : 'Couper le son'}
                  >
                    {isMuted ? <VolumeX aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
                  </button>
                  <label className="sr-only" htmlFor={`volume-${technique.id}`}>Volume</label>
                  <input
                    id={`volume-${technique.id}`}
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-20 accent-primary-500"
                  />
                </div>

                <div className="relative">
                  <button
                    type="button"
                    className="media-control"
                    onClick={() => setShowSettings((current) => !current)}
                    aria-label="Régler la vitesse de lecture"
                    aria-expanded={showSettings}
                    title="Vitesse de lecture"
                  >
                    <Settings aria-hidden="true" />
                  </button>
                  {showSettings && (
                    <div className="playback-settings">
                      <p className="mb-2 text-xs font-extrabold text-gray-600">Vitesse de lecture</p>
                      <div className="grid grid-cols-4 gap-1.5">
                        {playbackRates.map((rate) => (
                          <button
                            key={rate}
                            type="button"
                            onClick={() => {
                              setPlaybackRate(rate)
                              setShowSettings(false)
                            }}
                            className={`speed-button ${playbackRate === rate ? 'speed-button-active' : ''}`}
                            aria-pressed={playbackRate === rate}
                          >
                            {rate}×
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="media-control"
                  onClick={resetPlayer}
                  aria-label="Réinitialiser la vidéo"
                  title="Réinitialiser"
                >
                  <RotateCcw aria-hidden="true" />
                </button>

                <span className="ml-auto rounded-full bg-white/10 px-2.5 py-1 text-xs font-extrabold text-white">
                  {playbackRate}×
                </span>
              </div>
            </div>
          </section>
        )}

        {technique.media.pdfUrl && (
          <section className="modal-section flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5" aria-labelledby="technique-pdf-title">
            <div className="flex items-center gap-3">
              <span className="card-icon" aria-hidden="true"><FileText /></span>
              <div>
                <p className="eyebrow !mb-0.5">Documentation</p>
                <h3 id="technique-pdf-title" className="text-lg font-extrabold text-gray-900">Support pédagogique PDF</h3>
              </div>
            </div>
            <PDFViewer url={technique.media.pdfUrl} title={technique.name} />
          </section>
        )}
      </div>
    </Modal>
  )
}

export default TechniqueModal
